import React, { useState } from 'react';
import { ToolsSidebar } from '@/components/tools/ToolsSidebar';
import { ImageWorkspace } from '@/components/tools/ImageWorkspace';
import { SidebarProvider } from '@/components/ui/sidebar';
import SEO from '@/components/SEO';
import { createBreadcrumbSchema } from '@/components/SEO';

export interface UploadedImage {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
  dimensions?: { width: number; height: number };
  format: string;
  dpi?: number;
  selected?: boolean;
}

export type ToolType = 
  | 'upload'
  | 'enhance'
  | 'background-remove'
  | 'format-convert'
  | 'resize'
  | 'compress'
  | 'filters'
  | 'watermark'
  | 'embroidery'
  | 'product-photo'
  | 'social-media'
  | 'favicon-generator'
  | 'projects'
  | 'presets'
  | 'history'
  | 'export';

const ImageToolsHub = () => {
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [activeTool, setActiveTool] = useState<ToolType>('upload');
  const [sessionId] = useState(() => crypto.randomUUID());

  return (
    <SidebarProvider>
      <SEO
        title="Free AI Image Tools Hub | Edit, Enhance & Convert"
        description="Complete suite of AI image tools. Enhance quality, remove backgrounds, convert formats, resize, compress, and add watermarks all in one place."
        keywords="ai image tools, photo editing tools, image converter, background remover, image resizer, watermark tool, free image editor"
        url="/image-tools"
        jsonLd={[
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Image Tools', url: '/image-tools' }
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'AI Image Tools Suite',
            applicationCategory: 'MultimediaApplication',
            operatingSystem: 'Web',
            description: 'Free online AI image tools for editing, enhancing, converting, and optimizing images.',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
            featureList: ['Image Enhancement', 'Background Removal', 'Format Conversion', 'Resize & Crop', 'Compression', 'Watermark', 'Embroidery Conversion', 'Social Media Optimization']
          }
        ]}
      />
      <div className="min-h-screen flex w-full bg-background">
        <ToolsSidebar activeTool={activeTool} onToolChange={setActiveTool} />
        <ImageWorkspace 
          images={images}
          setImages={setImages}
          activeTool={activeTool}
          sessionId={sessionId}
        />
      </div>
    </SidebarProvider>
  );
};

export default ImageToolsHub;
